import { useRef } from 'react';

// Tells a scanner burst from typing inside a quantity field. React's onKeyDown runs
// before useBarcodeBuffer's document listener, so a field that confirms on Enter would
// otherwise accept the scanner's Enter with the barcode digits still in it.
//
// Same rule as useBarcodeBuffer: at least `minLength` characters, each within `idleMs`
// of the last, followed by Enter. `key()` returns { previousValue } (the field value
// before the burst) when that Enter arrives, otherwise null.
export const createBurstTracker = ({ idleMs = 100, minLength = 6 } = {}) => {
  let code = '';
  let lastAt = 0;
  let previousValue = '';

  return {
    key(key, value, now = Date.now()) {
      const continues = code.length > 0 && now - lastAt <= idleMs;
      if (key === 'Enter') {
        const burst = continues && code.length >= minLength ? { previousValue } : null;
        code = '';
        lastAt = 0;
        return burst;
      }
      if (key.length !== 1) return null;
      if (continues) {
        code += key;
      } else {
        code = key;
        previousValue = value;
      }
      lastAt = now;
      return null;
    },
  };
};

// onKeyDown helper for a quantity input:
//   const guardScan = useScanBurstGuard(setText);
//   onKeyDown={(e) => { if (guardScan(e)) return; if (e.key === 'Enter') confirm(); }}
// Returns true (and puts the field back) when the key ends a scanner burst.
export const useScanBurstGuard = (restore) => {
  const trackerRef = useRef(null);
  if (!trackerRef.current) trackerRef.current = createBurstTracker();
  return (e) => {
    const burst = trackerRef.current.key(e.key, e.currentTarget.value);
    if (!burst) return false;
    e.preventDefault();
    restore(burst.previousValue);
    return true;
  };
};
